import { Suspense } from 'react';
import { Box, Loader, Center, Alert } from '@mantine/core';
import { Widget, useWidgetGroupId } from '@pulse-ui/widget-sdk';
import { ErrorBoundary } from 'react-error-boundary';

interface WidgetContainerProps {
  widgetId: string;
  instanceId: string;
  WidgetComponent: Widget;
  config?: Record<string, unknown>;
}

/**
 * Container for individual widget instances
 * Handles lazy loading, error boundaries, and suspense.
 * Group membership is read from the widget-sdk atom family (the single source of truth);
 * it is not stored in FlexLayout config.
 */
export function WidgetContainer({
  widgetId,
  instanceId,
  WidgetComponent,
  config,
}: WidgetContainerProps) {
  const groupId = useWidgetGroupId(instanceId);
  return (
    <Box h="100%" w="100%" data-tour-scope={instanceId}>
      <ErrorBoundary
        fallback={
          <Center h="100%">
            <Alert color="red" title="Widget Error">
              Failed to load widget: {widgetId}
            </Alert>
          </Center>
        }
      >
        <Suspense
          fallback={
            <Center h="100%">
              <Loader size="sm" />
            </Center>
          }
        >
          <WidgetComponent
            instanceId={instanceId}
            groupId={groupId}
            config={config}
          />
        </Suspense>
      </ErrorBoundary>
    </Box>
  );
}
